type WaitlistTimerDebugDetails = Record<string, unknown>;

const MAX_PREVIEW_LENGTH = 700;

export const waitlistTimerDebugEnabled = (): boolean => {
  if (process.env.NODE_ENV === 'test') return false;

  // Opt-in only: this logs player GUIDs and raw broadcast payloads, so it must
  // stay OFF by default in production. A developer enables it for a session via
  // `localStorage.WAITLIST_TIMER_DEBUG = '1'`.
  try {
    if (typeof window === 'undefined') return false;
    const flag = window.localStorage?.getItem('WAITLIST_TIMER_DEBUG');
    return flag === '1' || flag === 'true' || flag === 'on';
  } catch {
    return false;
  }
};

export const summarizeForWaitlistTimerLog = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return {
      type: 'array',
      length: value.length,
      first: summarizeForWaitlistTimerLog(value[0])
    };
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const players = Array.isArray(record.players) ? record.players : undefined;
    const response =
      record.response && typeof record.response === 'object'
        ? (record.response as Record<string, unknown>)
        : undefined;
    const responsePlayers =
      response && Array.isArray(response.players) ? response.players : undefined;
    return {
      type: 'object',
      keys: Object.keys(record).slice(0, 12),
      playersLength: players?.length,
      responseKeys: response ? Object.keys(response).slice(0, 12) : undefined,
      responsePlayersLength: responsePlayers?.length,
      firstPlayer: summarizeForWaitlistTimerLog(players?.[0] ?? responsePlayers?.[0])
    };
  }

  if (typeof value === 'string') {
    return value.length > MAX_PREVIEW_LENGTH
      ? `${value.slice(0, MAX_PREVIEW_LENGTH)}...<${value.length} chars>`
      : value;
  }

  return value;
};

type WaitlistTimerEntryLike = {
  id?: unknown;
  game_id?: unknown;
  gameId?: unknown;
  game_type?: unknown;
  gameType?: unknown;
  player_guid?: unknown;
  playerGuid?: unknown;
  position?: unknown;
  waitlist_count?: unknown;
  waitlistCount?: unknown;
  your_turn?: unknown;
  yourTurn?: unknown;
  available_seats?: unknown;
  availableSeats?: unknown;
  waitlistTimeout?: unknown;
  waitlist_time_out?: unknown;
  waitlistTimeOut?: unknown;
  wait_for_accept_start_time?: unknown;
  waitForAcceptStartTime?: unknown;
  bump_count?: unknown;
  bumpCount?: unknown;
  createdAt?: unknown;
  created_at?: unknown;
  updatedAt?: unknown;
  updated_at?: unknown;
};

const valueType = (value: unknown): string => {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
};

const waitlistField = (
  entry: WaitlistTimerEntryLike,
  ...keys: Array<keyof WaitlistTimerEntryLike>
): unknown => {
  for (const key of keys) {
    if (entry[key] !== undefined) return entry[key];
  }
  return undefined;
};

export const summarizeWaitlistTimerFieldsForLog = (entry: unknown): unknown => {
  if (!entry || typeof entry !== 'object') return null;
  const waitlistEntry = entry as WaitlistTimerEntryLike;

  const gameId = waitlistField(waitlistEntry, 'gameId', 'game_id');
  const gameType = waitlistField(waitlistEntry, 'gameType', 'game_type');
  const playerGuid = waitlistField(waitlistEntry, 'playerGuid', 'player_guid');
  const waitlistCount = waitlistField(waitlistEntry, 'waitlistCount', 'waitlist_count');
  const yourTurn = waitlistField(waitlistEntry, 'yourTurn', 'your_turn');
  const availableSeats = waitlistField(waitlistEntry, 'availableSeats', 'available_seats');
  const waitlistTimeOut = waitlistField(
    waitlistEntry,
    'waitlistTimeOut',
    'waitlistTimeout',
    'waitlist_time_out'
  );
  const waitForAcceptStartTime = waitlistField(
    waitlistEntry,
    'waitForAcceptStartTime',
    'wait_for_accept_start_time'
  );
  const bumpCount = waitlistField(waitlistEntry, 'bumpCount', 'bump_count');
  const createdAt = waitlistField(waitlistEntry, 'createdAt', 'created_at');
  const updatedAt = waitlistField(waitlistEntry, 'updatedAt', 'updated_at');

  return {
    rawKeys: Object.keys(waitlistEntry).slice(0, 20),
    id: waitlistEntry.id,
    gameId,
    gameIdType: valueType(gameId),
    gameType,
    gameTypeType: valueType(gameType),
    playerGuid,
    playerGuidType: valueType(playerGuid),
    position: waitlistEntry.position,
    positionType: valueType(waitlistEntry.position),
    waitlistCount,
    waitlistCountType: valueType(waitlistCount),
    yourTurn,
    yourTurnType: valueType(yourTurn),
    availableSeats,
    availableSeatsType: valueType(availableSeats),
    waitlistTimeOut,
    waitlistTimeOutType: valueType(waitlistTimeOut),
    waitForAcceptStartTime,
    waitForAcceptStartTimeType: valueType(waitForAcceptStartTime),
    bumpCount,
    bumpCountType: valueType(bumpCount),
    createdAt,
    updatedAt
  };
};

export const summarizeWaitlistEntryForTimerLog = (
  entry: WaitlistTimerEntryLike | null | undefined
): unknown =>
  entry
    ? {
        id: entry.id,
        gameId: entry.gameId,
        gameType: entry.gameType,
        playerGuid: entry.playerGuid,
        position: entry.position,
        waitlistCount: entry.waitlistCount,
        yourTurn: entry.yourTurn,
        availableSeats: entry.availableSeats,
        waitlistTimeOut: entry.waitlistTimeOut,
        waitForAcceptStartTime: entry.waitForAcceptStartTime,
        bumpCount: entry.bumpCount
      }
    : null;

export const waitlistTimerLog = (stage: string, details: WaitlistTimerDebugDetails = {}) => {
  if (!waitlistTimerDebugEnabled()) return;

  // eslint-disable-next-line no-console
  console.info(`[WaitlistTimer] ${stage}`, {
    at: new Date().toISOString(),
    ...details
  });
};
